import { useEffect, useRef, useCallback } from 'react';
import { analyticsEvents, trackEvent } from '../utils/analytics';

// Therapy session milestones based on research and user engagement patterns
const THERAPY_MILESTONES = [
  { minutes: 5, label: 'short_session' },
  { minutes: 8, label: 'brief_session' },
  { minutes: 10, label: 'quick_session' },
  { minutes: 12, label: 'compact_session' },
  { minutes: 15, label: 'medium_session' },
  { minutes: 18, label: 'standard_session' },
  { minutes: 20, label: 'regular_session' },
  { minutes: 25, label: 'moderate_session' },
  { minutes: 30, label: 'long_session' },
  { minutes: 40, label: 'substantial_session' },
  { minutes: 45, label: 'comprehensive_session' },
  { minutes: 60, label: 'extended_session' },
  { minutes: 75, label: 'intensive_session' },
  { minutes: 90, label: 'deep_session' },
  { minutes: 120, label: 'very_long_session' }
];

interface TherapyMilestoneTrackingState {
  startTracking: (frequency: number) => void;
  stopTracking: () => void;
  isTracking: boolean;
}

export const useTherapyMilestoneTracking = (): TherapyMilestoneTrackingState => {
  const startTimeRef = useRef<number | null>(null);
  const frequencyRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const achievedMilestonesRef = useRef<Set<number>>(new Set());
  const isTrackingRef = useRef<boolean>(false);

  const checkMilestones = () => {
    if (!startTimeRef.current) return;

    const elapsedMinutes = (Date.now() - startTimeRef.current) / (1000 * 60);
    
    // Check if any new milestones have been reached
    THERAPY_MILESTONES.forEach(milestone => {
      if (
        elapsedMinutes >= milestone.minutes && 
        !achievedMilestonesRef.current.has(milestone.minutes)
      ) {
        // Mark milestone as achieved
        achievedMilestonesRef.current.add(milestone.minutes);
        
        // Track the milestone with individual parameter name
        trackEvent(`therapy_milestone_${milestone.minutes}`, {
          category: 'engagement',
          label: 'therapy_duration',
          value: frequencyRef.current,
          session_duration_minutes: milestone.minutes,
          milestone_type: milestone.label,
          therapy_type: 'notched_white_noise'
        });
        
        // Log in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Therapy Milestone] ${milestone.label}: ${milestone.minutes} minutes`);
        }
      }
    });
  };

  const startTracking = useCallback((frequency: number) => {
    // Clear any existing tracking
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Set up new tracking session
    startTimeRef.current = Date.now();
    frequencyRef.current = frequency;
    isTrackingRef.current = true;
    achievedMilestonesRef.current.clear();
    
    // Track initial play event
    analyticsEvents.playNotchedNoise(frequency);
    
    // Check for milestones every 30 seconds to be responsive but not excessive
    intervalRef.current = setInterval(checkMilestones, 30000);
    
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Therapy Tracking] Started tracking session for frequency: ${frequency}Hz`);
    }
  }, []);

  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Track final session duration if we were tracking
    if (startTimeRef.current && isTrackingRef.current) {
      const totalDurationMinutes = (Date.now() - startTimeRef.current) / (1000 * 60);
      
      // Track session end event
      trackEvent('therapy_session_end', {
        category: 'engagement',
        label: 'therapy_completion',
        value: frequencyRef.current,
        total_duration_minutes: Math.round(totalDurationMinutes * 100) / 100, // Round to 2 decimal places
        milestones_achieved: achievedMilestonesRef.current.size,
        therapy_type: 'notched_white_noise'
      });
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Therapy Tracking] Stopped tracking. Duration: ${totalDurationMinutes.toFixed(2)} minutes, Milestones: ${achievedMilestonesRef.current.size}`);
      }
    }
    
    startTimeRef.current = null;
    isTrackingRef.current = false;
    achievedMilestonesRef.current.clear();
  }, []);

  // Handle page visibility changes to pause/resume tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isTrackingRef.current) {
        // Page became hidden, pause tracking
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        
        // Log pause event
        if (process.env.NODE_ENV === 'development') {
          console.log('[Therapy Tracking] Paused due to page visibility change');
        }
      } else if (!document.hidden && isTrackingRef.current && startTimeRef.current) {
        // Page became visible again, resume tracking
        intervalRef.current = setInterval(checkMilestones, 30000);
        
        // Log resume event
        if (process.env.NODE_ENV === 'development') {
          console.log('[Therapy Tracking] Resumed tracking');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Clean up on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // No dependencies needed since we're using refs

  return {
    startTracking,
    stopTracking,
    isTracking: isTrackingRef.current
  };
};